/**
 * Dearlove — dropdown birthstone à pastilles (vraies pierres du bandeau).
 *
 * Enrichit le <select> natif de dl-personalization-fields sans le remplacer :
 * le select reste la source de vérité (form Dawn, properties[...],
 * validation dl-bundle.js qui lit select.value). L'UI custom (bouton +
 * listbox) se synchronise dessus ; le focus programmatique sur le select
 * caché (validateSets) est redirigé vers le bouton.
 */

if (!customElements.get('dl-stone-select')) {
  customElements.define(
    'dl-stone-select',
    class DlStoneSelect extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('select');
        if (!this.select || this.querySelector('.dl-stone-select__toggle')) return;

        const field = this.closest('[data-dl-field]');
        this.fieldLabel = field ? field.dataset.label : 'Birthstone';
        this.placeholder = this.select.options[0] && this.select.options[0].disabled
          ? this.select.options[0].textContent
          : 'Select';

        this.buildToggle();
        this.buildPanel();
        // Re-sync après coup (restauration de form au back-nav, etc.)
        this.renderValue();

        this.select.setAttribute('tabindex', '-1');
        this.select.setAttribute('aria-hidden', 'true');
        this.select.addEventListener('focus', () => this.toggle.focus());
        // Le select natif reste la source de verite : toute ecriture dessus
        // (recopie piece 1 -> piece 2, restauration de form) redessine la pastille.
        this.select.addEventListener('change', () => this.renderValue());

        this.addEventListener('keydown', (e) => this.onKeydown(e));
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) this.close();
        });
      }

      get stoneOptions() {
        return Array.from(this.select.options).filter((o) => !o.disabled);
      }

      buildToggle() {
        this.toggle = document.createElement('button');
        this.toggle.type = 'button';
        this.toggle.className = 'dl-stone-select__toggle';
        this.toggle.setAttribute('aria-haspopup', 'listbox');
        this.toggle.setAttribute('aria-expanded', 'false');
        this.toggle.setAttribute('aria-label', this.fieldLabel);
        this.toggle.innerHTML =
          '<span class="dl-stone-select__value"></span>' +
          '<svg class="dl-stone-select__caret" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">' +
          '<path d="M1 1.5 6 6.5 11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        this.valueEl = this.toggle.querySelector('.dl-stone-select__value');
        this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
        this.appendChild(this.toggle);
        this.renderValue();
      }

      buildPanel() {
        this.panel = document.createElement('div');
        this.panel.className = 'dl-stone-select__panel';
        this.panel.hidden = true;
        const list = document.createElement('ul');
        list.className = 'dl-stone-select__list';
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', this.fieldLabel);
        this.stoneOptions.forEach((opt) => {
          const li = document.createElement('li');
          li.className = 'dl-stone-select__option';
          li.setAttribute('role', 'option');
          li.dataset.value = opt.value;
          li.appendChild(this.buildSwatchContent(opt, true));
          li.addEventListener('click', () => this.choose(opt.value));
          list.appendChild(li);
        });
        this.panel.appendChild(list);
        this.appendChild(this.panel);
      }

      get isOpen() {
        return !this.panel.hidden;
      }

      open() {
        this.panel.hidden = false;
        this.classList.add('is-open');
        this.toggle.setAttribute('aria-expanded', 'true');
        this.highlight(Math.max(0, this.selectedIndex()));
      }

      close() {
        if (!this.isOpen) return;
        this.panel.hidden = true;
        this.classList.remove('is-open');
        this.toggle.setAttribute('aria-expanded', 'false');
      }

      selectedIndex() {
        return this.stoneOptions.findIndex((o) => o.value === this.select.value);
      }

      highlight(index) {
        const items = this.panel.querySelectorAll('.dl-stone-select__option');
        items.forEach((li, i) => li.classList.toggle('is-active', i === index));
        this.activeIndex = index;
        if (items[index]) items[index].scrollIntoView({ block: 'nearest' });
      }

      choose(value) {
        this.select.value = value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.renderValue();
        this.close();
        this.toggle.focus();
      }

      buildSwatchContent(opt, lazy) {
        const frag = document.createDocumentFragment();
        const img = document.createElement('img');
        img.className = 'dl-stone-select__swatch';
        img.src = opt.dataset.swatch;
        img.alt = '';
        img.width = 96;
        img.height = 96;
        if (lazy) {
          img.loading = 'lazy';
          img.decoding = 'async';
        }
        const label = document.createElement('span');
        label.textContent = opt.textContent;
        frag.appendChild(img);
        frag.appendChild(label);
        return frag;
      }

      renderValue() {
        if (!this.valueEl || !this.panel) return;
        const opt = this.stoneOptions.find((o) => o.value === this.select.value);
        this.valueEl.textContent = '';
        if (!opt) {
          const ph = document.createElement('span');
          ph.className = 'dl-stone-select__placeholder';
          ph.textContent = this.placeholder;
          this.valueEl.appendChild(ph);
          return;
        }
        this.valueEl.appendChild(this.buildSwatchContent(opt, false));
        this.panel.querySelectorAll('.dl-stone-select__option').forEach((li) => {
          li.setAttribute('aria-selected', li.dataset.value === opt.value ? 'true' : 'false');
        });
      }

      onKeydown(e) {
        const options = this.stoneOptions;
        switch (e.key) {
          case 'Escape':
            this.close();
            this.toggle.focus();
            break;
          case 'ArrowDown':
          case 'ArrowUp': {
            e.preventDefault();
            if (!this.isOpen) {
              this.open();
              break;
            }
            const delta = e.key === 'ArrowDown' ? 1 : -1;
            this.highlight(Math.min(options.length - 1, Math.max(0, this.activeIndex + delta)));
            break;
          }
          case 'Enter':
          case ' ':
            if (this.isOpen) {
              e.preventDefault();
              if (options[this.activeIndex]) this.choose(options[this.activeIndex].value);
            } else if (e.target === this.toggle && e.key === 'Enter') {
              e.preventDefault();
              this.open();
            }
            break;
          case 'Tab':
            this.close();
            break;
        }
      }
    }
  );
}
